import { Side } from '@/compiled_proto/com/celertech/positionmanager/api/enums/SideProto';
import { formatToDate, getOperationalCurrentTradeDate } from '@/helpers/dateHelper';
import { CfdTradeFeePreview, FeePreviewResponse, SpotTradeFeePreview } from '@/types/sso';
import BigNumber from 'bignumber.js';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchPortalAPI, usePortalSWR, useSso } from '../providers/SSOProvider';
import { getCurrencyPrices, UseOrderExecutionReturn } from './useOrderExecution';

export interface TradeFeePreviewParams extends UseOrderExecutionReturn {
    side: Side;
    isCfd: boolean;
    spotPayload?: SpotTradeFeePreview;
    cfdPayload?: CfdTradeFeePreview;
    debouncedQuantity: number | null;
}

export type UseTradeFeePreviewParams = TradeFeePreviewParams;

export interface UseTradeFeePreviewReturn {
    cfdFeesApi: ReturnType<typeof usePortalSWR<FeePreviewResponse[]>>;
    spotFeesApi: ReturnType<typeof usePortalSWR<FeePreviewResponse[]>>;
    refreshCfdFees: () => void;
    refreshSpotFees: () => void;
}

const useTradeFeePreview = (props: UseTradeFeePreviewParams): UseTradeFeePreviewReturn => {
    const { orderBook, instrument, ccy1Config, ccy2Config, isCcy2Order, ...restPreviewProps } = props;

    const { bestPrice } = orderBook;
    const { side, isCfd, debouncedQuantity } = restPreviewProps;

    const { foundEntity, onFetchJWT, jwt } = useSso();

    const spotPayload = useMemo(() => {
        if (debouncedQuantity) {
            const { ccy1Quantity, ccy2Quantity } = getCurrencyPrices(
                side,
                instrument,
                debouncedQuantity,
                bestPrice,
                isCcy2Order
            );

            return {
                currencyOneId: ccy1Config.id,
                currencyTwoId: ccy2Config.id,
                entityId: foundEntity?.entityId,
                price: bestPrice,
                currencyOneQuantity: ccy1Quantity
                    ? BigNumber(ccy1Quantity).decimalPlaces(ccy1Config.order_decimals).toNumber()
                    : 0,
                currencyTwoQuantity: ccy2Quantity
                    ? BigNumber(ccy2Quantity).decimalPlaces(ccy2Config.order_decimals).toNumber()
                    : 0,
                settlementDate: orderBook?.settlementDate
                    ? formatToDate(new Date(orderBook?.settlementDate))
                    : undefined,
                tradeDate: formatToDate(getOperationalCurrentTradeDate())
            };
        }
    }, [
        side,
        ccy1Config,
        ccy2Config,
        isCcy2Order,
        foundEntity,
        bestPrice,
        debouncedQuantity,
        orderBook?.settlementDate
    ]);

    const cfdPayload = useMemo(() => {
        if (debouncedQuantity) {
            let quantity;
            if (side === Side.BUY) quantity = debouncedQuantity;
            else if (side === Side.SELL) quantity = BigNumber(debouncedQuantity).negated().toNumber();

            return {
                cfdInstrumentId: instrument.id,
                entityId: foundEntity?.entityId,
                price: bestPrice,
                quantity,
                settlementDate: orderBook?.settlementDate
                    ? formatToDate(new Date(orderBook?.settlementDate))
                    : undefined,
                tradeDate: formatToDate(getOperationalCurrentTradeDate()),
                settlementCurrencyId: ccy2Config.id
            };
        }
    }, [
        side,
        ccy1Config,
        ccy2Config,
        isCcy2Order,
        foundEntity,
        bestPrice,
        debouncedQuantity,
        orderBook?.settlementDate
    ]);

    const [prevSpotPayload, setPrevSpotPayload] = useState(spotPayload);
    const [prevCfdPayload, setPrevCfdPayload] = useState(cfdPayload);

    const canLoadApi = useMemo(() => {
        if (isCfd) return Boolean(prevCfdPayload?.price && prevCfdPayload?.quantity && prevCfdPayload?.cfdInstrumentId);
        else
            return Boolean(
                prevSpotPayload?.currencyOneId &&
                    prevSpotPayload?.currencyTwoId &&
                    prevSpotPayload?.entityId &&
                    prevSpotPayload?.price &&
                    prevSpotPayload?.currencyOneQuantity &&
                    prevSpotPayload?.currencyTwoQuantity &&
                    prevSpotPayload?.settlementDate &&
                    prevSpotPayload?.tradeDate
            );
    }, [isCfd, prevSpotPayload, prevCfdPayload]);

    const cfdFeesApi = usePortalSWR<FeePreviewResponse[]>('/api/trades/fees/preview/cfd', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && canLoadApi && isCfd ? '/api/trades/fees/preview/cfd' : undefined,
            jwt,
            prevCfdPayload,
            'POST'
        )
    );

    const spotFeesApi = usePortalSWR<FeePreviewResponse[]>('/api/trades/fees/preview/spot', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && canLoadApi && !isCfd ? '/api/trades/fees/preview/spot' : undefined,
            jwt,
            prevSpotPayload,
            'POST'
        )
    );

    const refreshCfdFees = useCallback(() => {
        if (foundEntity && canLoadApi && isCfd) {
            cfdFeesApi.mutate();
        }
    }, [foundEntity, canLoadApi, isCfd, cfdFeesApi]);

    const refreshSpotFees = useCallback(() => {
        if (foundEntity && canLoadApi && !isCfd) {
            spotFeesApi.mutate();
        }
    }, [foundEntity, canLoadApi, isCfd, spotFeesApi]);

    const movementsRef = useRef<any[]>([]);
    const previousPriceRef = useRef(bestPrice);

    // stores cumulative movements

    useEffect(() => {
        if (debouncedQuantity) {
            const THRESHOLD_PIPS = 100;
            // 5 minute rolling window
            const TIMEFRAME_MS = 5 * 60 * 1000;

            const pipValue = 1 / Math.pow(10, instrument.pip_size || 1);

            // final cumulative threshold
            const cumulativeThreshold = pipValue * THRESHOLD_PIPS;

            const previous = previousPriceRef.current;

            // absolute movement since last tick
            const movement = BigNumber(bestPrice).minus(previous).toNumber();

            const now = Date.now();

            // add latest movement
            movementsRef.current.push({
                value: movement,
                timestamp: now
            });

            // remove old movements outside timeframe
            movementsRef.current = movementsRef.current.filter((tick) => now - tick.timestamp <= TIMEFRAME_MS);

            // calculate cumulative movement
            const cumulativeMovement = movementsRef.current.reduce(
                (sum, tick) => BigNumber(sum).plus(tick.value).toNumber(),
                0
            );

            // console.log({
            //     movement,
            //     cumulativeMovement: BigNumber(cumulativeMovement).abs().toNumber(),
            //     pipSize: instrument.pip_size,
            //     cumulativeThreshold
            // });
            // trigger condition
            if (BigNumber(cumulativeMovement).abs().toNumber() >= cumulativeThreshold) {
                refreshCfdFees();
                refreshSpotFees();
                // reset after trigger (optional)
                movementsRef.current = [];
            }

            previousPriceRef.current = bestPrice;
        }
    }, [bestPrice]);

    useEffect(() => {
        movementsRef.current = [];
        previousPriceRef.current = bestPrice;
        if (!debouncedQuantity) {
            spotFeesApi.mutate(undefined, { revalidate: false });
            cfdFeesApi.mutate(undefined, { revalidate: false });
        }
    }, [debouncedQuantity]);

    useEffect(() => {
        if (_.isEqual(prevSpotPayload, spotPayload) && _.isEqual(prevCfdPayload, cfdPayload)) {
            return;
        }
        setPrevSpotPayload(spotPayload);
        setPrevCfdPayload(cfdPayload);
    }, [debouncedQuantity]);

    useEffect(() => {
        if (foundEntity && canLoadApi && isCfd) {
            cfdFeesApi.mutate();
        }
        if (foundEntity && canLoadApi && !isCfd) {
            spotFeesApi.mutate();
        }
    }, [prevCfdPayload, prevSpotPayload]);

    return {
        cfdFeesApi,
        spotFeesApi,
        refreshCfdFees,
        refreshSpotFees
    };
};

export default useTradeFeePreview;
